// legal-api.js — Legal module data access.
//
// ARCHITECTURE: the browser NEVER calls a third-party legal API directly and
// NEVER holds an API key. Every data call goes through the VaultSync server-side
// connector proxy, which holds the central key (or the per-account BYOK key) and
// returns a NORMALIZED payload. This file is the thin client over that proxy.
//
//   POST /api/worklog/datasource/{connector}  { params:{...} }   Bearer gdbs_token
//     -> { ok:true,  result:<normalized payload> }
//     -> { ok:false, reason:<code>[, note] }
//
//   reason codes the UI handles: "error" | "not-configured" | "rate-limited"
//                                | "unsupported" | "quota-exhausted"
//
// BYOK (per-account, server-side, encrypted; booleans/write-only — never reads a key):
//   GET    /api/worklog/datasource/mykeys              -> { providers:[{provider,set}] }
//   POST   /api/worklog/datasource/mykeys { provider, key }   (echoes only provider)
//   DELETE /api/worklog/datasource/mykeys/{provider}
//
// There is intentionally NO getApiKey/setApiKey and NO localStorage('gdbs_legal_key_*')
// here: client-held third-party keys were removed.

// ==========================================================================
// Proxy plumbing
// ==========================================================================
function authHeaders() {
  const token = localStorage.getItem('gdbs_token');
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
  };
}

/**
 * Call a server-side connector. Returns the proxy envelope verbatim:
 *   { ok:true, result } | { ok:false, reason[, note] }
 * Never throws for a connector-level failure (not-configured / rate-limited /
 * quota-exhausted / unsupported / error) — those are states the UI renders.
 * Only a transport/JSON fault collapses to { ok:false, reason:'error' }.
 */
export async function callConnector(connector, params = {}) {
  try {
    const res = await fetch(`/api/worklog/datasource/${encodeURIComponent(connector)}`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ params })
    });
    // The proxy returns 200 for connector-level failures; a non-200 is a real
    // transport/auth fault. Try to read a body either way.
    let data = null;
    try { data = await res.json(); } catch (_e) { /* non-JSON */ }

    if (data && typeof data.ok === 'boolean') return data;
    if (!res.ok) return { ok: false, reason: 'error', note: `HTTP ${res.status}` };
    return { ok: false, reason: 'error' };
  } catch (err) {
    return { ok: false, reason: 'error', note: err && err.message ? err.message : 'network error' };
  }
}

// ==========================================================================
// BYOK — per-account keys, server-side + encrypted. Never returns a key value.
// providers: "uspto" | "apidatagov" | "courtlistener"
// ==========================================================================
export async function getMyKeys() {
  try {
    const res = await fetch('/api/worklog/datasource/mykeys', { headers: authHeaders() });
    if (!res.ok) return { providers: [] };
    const data = await res.json();
    return { providers: data.providers || [] };
  } catch (_e) {
    return { providers: [] };
  }
}

export async function setMyKey(provider, key) {
  try {
    const res = await fetch('/api/worklog/datasource/mykeys', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ provider, key })
    });
    const data = await res.json().catch(() => null);
    return { ok: res.ok && !!(data && (data.ok || data.provider)), provider };
  } catch (_e) {
    return { ok: false, provider };
  }
}

export async function deleteMyKey(provider) {
  try {
    const res = await fetch(`/api/worklog/datasource/mykeys/${encodeURIComponent(provider)}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    return { ok: res.ok, provider };
  } catch (_e) {
    return { ok: false, provider };
  }
}

// ==========================================================================
// Connector wrappers — each returns the proxy envelope { ok, result|reason }.
// The mapped connector names + normalized payload shapes are per the proxy
// contract (see docs/development/legal_module.txt).
// ==========================================================================

// Prior Art -> uspto.patents
// result: { count, results:[{title,patentNumber,inventor?,date,url}] }
export function searchPatents(query, perPage = 10) {
  return callConnector('uspto.patents', { q: query, per_page: Math.min(perPage, 25) });
}

// Compliance -> ecfr.search
// result: { count, results:[{title,hierarchy,url}] }
export function searchCFR(query) {
  return callConnector('ecfr.search', { query });
}

// Trademark -> uspto.trademarks (ALWAYS { ok:false, reason:'unsupported', note })
export function searchTrademarks(query) {
  return callConnector('uspto.trademarks', { q: query });
}

// RECAP / case filings + opinions -> courtlistener.search (429 -> reason 'rate-limited')
// result: { count, results:[{caseName,court,dateFiled,citation?,url}] }
export function searchCourtListener(query) {
  return callConnector('courtlistener.search', { q: query });
}

// SCOTUS local full-text index -> caselaw.scotus
// result: { count, results:[{id,caseName,citation,dateFiled,snippet,score}], note? }
export function searchScotus(query, limit = 20) {
  return callConnector('caselaw.scotus', { query, limit });
}

// Federal Register -> federalregister.search
// result: { count, results:[{title,type,publication_date,html_url,agency}] }
export function searchFederalRegister(query, perPage = 20) {
  return callConnector('federalregister.search', { q: query, per_page: Math.min(perPage, 20) });
}

// Sanctions -> ofac.screen
// result: { asOf, query, count, matches:[{name,type,programs,score,sdnId}] }
export function screenSanctions(name, minScore) {
  const params = { name };
  if (minScore != null) params.minScore = minScore;
  return callConnector('ofac.screen', params);
}

// Congress -> congress.bills
// result: { count, results:[{billNumber,congress,title,latestAction,url}] }
export function searchCongress(query, perPage = 10) {
  return callConnector('congress.bills', { q: query, per_page: Math.min(perPage, 20) });
}

// ==========================================================================
// Legal document generation — authenticated backend, with a client-side
// fallback builder that assembles a professional HTML doc straight from the
// (already-normalized) proxy result data. No third-party call, no key.
// ==========================================================================

/**
 * type: 'prior_art' | 'compliance' | 'itar' | 'expert_report'
 * Returns { document: string (HTML), citations: string[] }
 */
export async function generateLegalDocument(type, context, apiData) {
  const token = localStorage.getItem('gdbs_token');
  try {
    const res = await fetch('/api/legal/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': 'Bearer ' + token } : {})
      },
      body: JSON.stringify({ type, context, apiData })
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (data.success === false) throw new Error(data.error || 'Generation failed');
    return { document: data.document || data.html || '', citations: data.citations || [] };
  } catch (_err) {
    return buildFallbackDocument(type, context, apiData);
  }
}

// --------------------------------------------------------------------------
// Fallback document builder — produces professional HTML from proxy result data
// --------------------------------------------------------------------------
function buildFallbackDocument(type, context, apiData) {
  const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const citations = [];

  let title = '';
  let body = '';

  switch (type) {
    case 'prior_art':
      title = 'Prior Art Analysis Report';
      body = buildPriorArtDoc(context, apiData, citations);
      break;
    case 'compliance':
      title = 'Regulatory Compliance Matrix';
      body = buildComplianceDoc(context, apiData, citations);
      break;
    case 'itar':
      title = 'Export Control Classification Memorandum';
      body = buildItarDoc(context, apiData, citations);
      break;
    case 'expert_report':
      title = 'Technical Expert Report';
      body = buildExpertReportDoc(context, apiData, now);
      break;
    default:
      title = 'Legal Analysis Document';
      body = buildGenericDoc(context, apiData);
  }

  const document = `
<div style="font-family:'Segoe UI',system-ui,sans-serif;color:#e2e8f0;line-height:1.7;max-width:800px;">
  <div style="border-bottom:2px solid #4fc3f7;padding-bottom:16px;margin-bottom:24px;">
    <h1 style="color:#4fc3f7;font-size:20px;margin:0 0 6px;">${escHtml(title)}</h1>
    <div style="color:#64748b;font-size:12px;">Generated: ${escHtml(now)} &nbsp;|&nbsp; GDBS Legal Module &nbsp;|&nbsp; <em>Not legal advice - consult qualified counsel</em></div>
  </div>
  ${body}
</div>`.trim();

  return { document, citations };
}

function buildPriorArtDoc(context, apiData, citations) {
  // Normalized patents: [{title,patentNumber,inventor?,date,url}]
  const patents = apiData?.patents || [];
  let html = `
<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">1. Technology Summary</h2>
  <p style="color:#cbd5e1;font-size:13px;">${escHtml(context || 'No technology description provided.')}</p>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">2. Prior Art Search Results</h2>
  <p style="color:#94a3b8;font-size:12px;margin-bottom:12px;">${patents.length} patent${patents.length !== 1 ? 's' : ''} identified via USPTO (server-side proxy).</p>`;

  if (patents.length === 0) {
    html += `<p style="color:#64748b;font-size:12px;font-style:italic;">No prior art patents returned for this query. Consider broadening search terms.</p>`;
  } else {
    patents.forEach((p, i) => {
      const patNum = p.patentNumber || '';
      const citation = `US ${patNum ? 'Patent ' + patNum : 'Application'} - ${p.title} (${p.date || ''})`;
      citations.push(citation);
      const url = p.url || (patNum ? `https://patents.google.com/patent/US${escHtml(patNum)}` : 'https://ppubs.uspto.gov/pubwebapp/');
      html += `
  <div style="background:#0d1117;border:1px solid #1e293b;border-radius:6px;padding:14px;margin-bottom:10px;">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:4px;">
      <div style="color:#4fc3f7;font-size:13px;font-weight:600;">[${i + 1}] ${escHtml(p.title)}</div>
      <div style="color:#475569;font-size:11px;white-space:nowrap;">${escHtml(p.date || '')}</div>
    </div>
    <div style="color:#64748b;font-size:11px;margin:4px 0;">
      ${patNum ? 'No. ' + escHtml(patNum) : ''}${p.inventor ? ' &nbsp;·&nbsp; ' + escHtml(p.inventor) : ''}
    </div>
    <div style="margin-top:8px;"><a href="${url}" target="_blank" rel="noopener" style="color:#4fc3f7;font-size:11px;">View patent →</a></div>
  </div>`;
    });
  }

  html += `</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">3. Patentability Assessment</h2>
  <p style="color:#cbd5e1;font-size:13px;">Based on the search results above, a preliminary assessment of novelty and non-obviousness should be conducted by qualified patent counsel. The identified prior art should be reviewed for:</p>
  <ul style="color:#94a3b8;font-size:12px;line-height:1.8;padding-left:20px;">
    <li>Anticipation under 35 U.S.C. § 102 (novelty)</li>
    <li>Obviousness under 35 U.S.C. § 103</li>
    <li>Subject matter eligibility under 35 U.S.C. § 101</li>
  </ul>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">4. Citations</h2>
  <ol style="color:#94a3b8;font-size:12px;line-height:1.8;padding-left:20px;">
    ${citations.map(c => `<li>${escHtml(c)}</li>`).join('')}
  </ol>
</section>`;

  return html;
}

function buildComplianceDoc(context, apiData, citations) {
  // Normalized regs: [{title,hierarchy,url}]
  const regs = apiData?.regulations || [];
  const params = apiData?.params || '';

  let html = `
<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">1. System Description</h2>
  <p style="color:#cbd5e1;font-size:13px;">${escHtml(context || 'No description provided.')}</p>
  ${params ? `<p style="color:#94a3b8;font-size:12px;"><strong style="color:#cbd5e1;">Key Parameters:</strong> ${escHtml(params)}</p>` : ''}
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">2. Applicable Regulatory Provisions</h2>`;

  if (regs.length === 0) {
    html += `<p style="color:#64748b;font-size:12px;font-style:italic;">No regulations returned for this query.</p>`;
  } else {
    html += `<table style="width:100%;border-collapse:collapse;font-size:12px;">
      <thead>
        <tr style="background:#1e293b;">
          <th style="padding:8px 10px;text-align:left;color:#94a3b8;border-bottom:1px solid #334155;white-space:normal;">Citation / Hierarchy</th>
          <th style="padding:8px 10px;text-align:left;color:#94a3b8;border-bottom:1px solid #334155;white-space:normal;">Title / Subject</th>
        </tr>
      </thead>
      <tbody>`;
    regs.forEach((r, i) => {
      const citation = r.hierarchy || `Regulation ${i + 1}`;
      citations.push(`${citation} - ${r.title || ''}`);
      html += `
        <tr style="border-bottom:1px solid #1e293b;">
          <td style="padding:8px 10px;color:#a78bfa;vertical-align:top;white-space:normal;">${escHtml(citation)}</td>
          <td style="padding:8px 10px;color:#e2e8f0;vertical-align:top;white-space:normal;">${escHtml(r.title || '')}</td>
        </tr>`;
    });
    html += `</tbody></table>`;
  }

  html += `</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">3. Compliance Determination</h2>
  <p style="color:#cbd5e1;font-size:13px;">A formal compliance determination requires review of each applicable provision against the system parameters listed above. This matrix should be reviewed by qualified regulatory counsel prior to submission or deployment.</p>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">4. Citations</h2>
  <ol style="color:#94a3b8;font-size:12px;line-height:1.8;padding-left:20px;">
    ${citations.map(c => `<li>${escHtml(c)}</li>`).join('')}
  </ol>
</section>`;

  return html;
}

function buildItarDoc(context, apiData, citations) {
  // Normalized CFR-like provisions for ITAR/EAR: reuse ecfr.search result rows
  // [{title,hierarchy,url}]
  const ccl = apiData?.cclResults || [];
  const origin = apiData?.origin || 'Unknown';
  const destination = apiData?.destination || 'Unknown';
  const dualUse = apiData?.dualUse || false;

  let html = `
<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">1. Technology Description</h2>
  <p style="color:#cbd5e1;font-size:13px;">${escHtml(context || 'No description provided.')}</p>
  <table style="margin-top:12px;font-size:12px;border-collapse:collapse;width:100%;">
    <tr><td style="color:#64748b;padding:4px 12px 4px 0;width:140px;white-space:normal;">Origin Country</td><td style="color:#e2e8f0;white-space:normal;">${escHtml(origin)}</td></tr>
    <tr><td style="color:#64748b;padding:4px 12px 4px 0;white-space:normal;">Destination</td><td style="color:#e2e8f0;white-space:normal;">${escHtml(destination)}</td></tr>
    <tr><td style="color:#64748b;padding:4px 12px 4px 0;white-space:normal;">Dual-Use Flag</td><td style="color:${dualUse ? '#fbbf24' : '#34d399'};white-space:normal;">${dualUse ? 'Yes - enhanced scrutiny required' : 'No'}</td></tr>
  </table>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">2. Regulatory Search Results (EAR/ITAR)</h2>`;

  if (ccl.length === 0) {
    html += `<p style="color:#64748b;font-size:12px;font-style:italic;">No matching CCL/USML provisions found for this query.</p>`;
  } else {
    ccl.forEach((r, i) => {
      const citation = r.hierarchy || `Section ${i + 1}`;
      citations.push(`${citation} - ${r.title || ''}`);
      html += `
  <div style="background:#0d1117;border:1px solid #1e293b;border-radius:6px;padding:12px;margin-bottom:10px;">
    <div style="color:#a78bfa;font-size:12px;font-weight:600;margin-bottom:4px;">${escHtml(citation)}</div>
    <div style="color:#e2e8f0;font-size:13px;margin:4px 0;">${escHtml(r.title || '')}</div>
    ${r.url ? `<a href="${escHtml(r.url)}" target="_blank" rel="noopener" style="color:#4fc3f7;font-size:11px;">eCFR →</a>` : ''}
  </div>`;
    });
  }

  html += `</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">3. Preliminary Classification Assessment</h2>
  <div style="background:#1e293b;border-left:3px solid #fbbf24;padding:12px 16px;border-radius:0 6px 6px 0;margin-bottom:14px;">
    <p style="color:#fbbf24;font-size:12px;font-weight:600;margin:0 0 4px;">NOTICE</p>
    <p style="color:#cbd5e1;font-size:12px;margin:0;">This is a preliminary screening tool only. Export classification determinations under the EAR (15 CFR Parts 730-774) and ITAR (22 CFR Parts 120-130) must be made by qualified export control counsel. An incorrect classification can result in civil penalties up to $1,000,000 per violation and criminal penalties under the Arms Export Control Act.</p>
  </div>
  <p style="color:#cbd5e1;font-size:13px;">Next steps: (1) Identify ECCN or USML category; (2) Determine license requirement or exception applicability; (3) Screen parties against Denied Parties List (DPL), Entity List, SDN list; (4) File EEI in AES if required.</p>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">4. Citations</h2>
  <ol style="color:#94a3b8;font-size:12px;line-height:1.8;padding-left:20px;">
    ${citations.map(c => `<li>${escHtml(c)}</li>`).join('')}
  </ol>
</section>`;

  return html;
}

function buildExpertReportDoc(context, apiData, now) {
  const reportType = apiData?.reportType || 'Technical Report';
  const reportTitle = apiData?.reportTitle || 'Expert Technical Report';
  const reportAuthor = apiData?.reportAuthor || '';

  return `
<section style="margin-bottom:24px;background:#1e293b;padding:16px;border-radius:6px;">
  <table style="font-size:12px;border-collapse:collapse;width:100%;">
    <tr><td style="color:#64748b;padding:3px 16px 3px 0;width:120px;white-space:normal;">Document Type</td><td style="color:#e2e8f0;white-space:normal;">${escHtml(reportType)}</td></tr>
    <tr><td style="color:#64748b;padding:3px 16px 3px 0;white-space:normal;">Prepared By</td><td style="color:#e2e8f0;white-space:normal;">${escHtml(reportAuthor || 'GDBS Legal Module')}</td></tr>
    <tr><td style="color:#64748b;padding:3px 16px 3px 0;white-space:normal;">Date</td><td style="color:#e2e8f0;white-space:normal;">${escHtml(now)}</td></tr>
    <tr><td style="color:#64748b;padding:3px 16px 3px 0;white-space:normal;">Re:</td><td style="color:#e2e8f0;white-space:normal;">${escHtml(reportTitle)}</td></tr>
  </table>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">I. Qualifications and Basis of Opinion</h2>
  <p style="color:#cbd5e1;font-size:13px;">This report is generated from computational analysis performed using the GDBS scientific computing platform. The underlying calculations employ GeoNum precision arithmetic validated against peer-reviewed benchmarks. All computations are reproducible and methodology is disclosed herein.</p>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">II. Technical Background</h2>
  <p style="color:#cbd5e1;font-size:13px;">${escHtml(context || 'No technical context provided.')}</p>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">III. Analysis and Findings</h2>
  <p style="color:#cbd5e1;font-size:13px;">The following analysis is based on the computational results and regulatory/patent data assembled using the GDBS Legal module. Findings are organized by relevance to the subject matter described above.</p>
  <p style="color:#94a3b8;font-size:12px;font-style:italic;">[Attach simulation outputs, charts, and numerical results from the relevant GDBS module tabs to support this section.]</p>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">IV. Conclusions</h2>
  <p style="color:#cbd5e1;font-size:13px;">Based on the foregoing analysis, it is my opinion that [insert conclusion]. This opinion is stated to a reasonable degree of scientific certainty.</p>
</section>

<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">V. Certification</h2>
  <p style="color:#cbd5e1;font-size:13px;">I declare under penalty of perjury that the foregoing is true and correct to the best of my knowledge. The opinions expressed are my own and are not influenced by the outcome of any proceeding.</p>
  <div style="margin-top:24px;border-top:1px solid #334155;padding-top:12px;">
    <p style="color:#e2e8f0;font-size:12px;">Signature: _____________________________&nbsp;&nbsp;&nbsp;&nbsp;Date: ${escHtml(now)}</p>
    ${reportAuthor ? `<p style="color:#94a3b8;font-size:12px;">${escHtml(reportAuthor)}</p>` : ''}
  </div>
</section>`;
}

function buildGenericDoc(context, apiData) {
  return `
<section style="margin-bottom:20px;">
  <h2 style="color:#94a3b8;font-size:14px;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #1e293b;padding-bottom:8px;">Analysis</h2>
  <p style="color:#cbd5e1;font-size:13px;">${escHtml(context || '')}</p>
  <pre style="background:#0d1117;padding:12px;border-radius:6px;color:#64748b;font-size:11px;overflow-x:auto;margin-top:12px;">${escHtml(JSON.stringify(apiData, null, 2))}</pre>
</section>`;
}

// Minimal HTML escape — keeps output safe when injecting strings
function escHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
